import { FC, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import { getFSCustomers } from '@/libs/fleet-api/services/customer';
import { useQuery } from '@tanstack/react-query';
import { FleetPagination } from '@/libs/fleet-api/fetcher';
import { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';
import { useModals } from '@/components/modals/ModalProvider';

const FSCustomerListPage: FC = () => {
	const { navigate } = useApp();
	const { openCustomerDetail } = useModals();
	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
	});
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getFSCustomers', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getFSCustomers(queryParams);
		},
	});

	const dataSource = data?.data.items ?? [];

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>List Verifikasi Dokumen Referral Join Financing</FleetTitle>

			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleSearch}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={<span>Search By</span>}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								placeholder="Search By"
								options={[
									{
										label: 'Nama Customer',
										value: 'customer-name',
									},
									{
										label: 'NPWP',
										value: 'npwp',
									},
								]}
								allowClear
								onChange={(value) => {
									if (value === 'my-customer') {
										setSearchValue(null);
									}
									setSearchBy(value);
								}}
								onClear={() => {
									setQueryParams((current) => {
										return {
											...current,
											search: undefined,
										};
									});
									setSearchBy(null);
								}}
								style={{
									display: 'block',
									width: '100%',
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item>
							<Input
								placeholder="Search..."
								style={{
									display: 'block',
								}}
								disabled={searchBy === null || searchBy === 'my-customer'}
								value={searchValue!}
								onChange={(e) => {
									const value = e.target.value;
									setSearchValue(value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								block
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				bordered
				loading={isLoading || isFetching}
				scroll={{ x: 'max-content' }}
				dataSource={dataSource.map((data, key) => {
					return { ...data, key };
				})}
				columns={[
					{
						title: 'No',
						width: 1,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'Nama Customer',
						render: (_, record) => (
							<a
								href="#"
								title={record.customerName}
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'NPWP',
						render: (_, record) => record.npwp,
					},
					{
						title: 'Group Customer BMPP',
						render: (_, record) => record.customerGroup,
					},

					{
						title: 'Nama FBDH',
						render: (_, record) => record.reportTo,
					},
					{
						title: 'Nama RM',
						render: (_, record) => record.createdBy,
					},
					{
						title: 'New Order Date',
						render: (_, record) => dayjs(record.createdAt).format('DD/MM/YYYY'),
					},
					{
						title: 'Select',
						align: 'center',
						width: 1,
						render: (_, record) => (
							<a
								style={{ display: 'block' }}
								onClick={(e) => {
									e.preventDefault();
									navigate('FSCustomerPage', {
										customerId: record.customerId,
									});
								}}
							>
								<ArrowRightOutlined />
							</a>
						),
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => {
							return {
								pageSize,
								pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
							};
						});
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
			/>
		</Space>
	);
};

export default FSCustomerListPage;
